import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Stack, Button, Text, Center, Group, Box } from '@mantine/core';
import WindowChoiceMobile from '../components/WindowChoice.mobile';
import HouseMaterialMobile from '../components/HouseMaterial.mobile';
import HouseSizeMobile from '../components/HouseSize.mobile';
import PreviewSVG from '../components/PreviewSVG';
import houseButton from '../images/ui_buttons/house_button.png';
import fenceButton from '../images/ui_buttons/fence_button.png';
import { Constants, colors, fontSize } from '../constants';

const { white } = colors;

const HouseEditorMobile = ({
	// State functions
	isHouseEditorDone,
	showFacadeChoiceButtons,
	facade,
	houseSize,
	windows,
	
	// Handle functions
	handleNext,
	handleBack,
	handleHouseClick,
	handleFenceClick,
	handleNoneClick,
	handleBackToFacadeChoice,
	handleFacadeSelection,
	handleHouseSizeSelection,
	handleWindowSelection
}) => {
	// Handle null values gracefully
	const safeFacade = facade || null;
	
	// Ref for measuring container size
	const containerRef = useRef(null);

	// Step state management - handled entirely within mobile component
	const [currentStep, setCurrentStep] = useState(0); // 0: material, 1: size, 2: window

	// Step navigation handlers
	const handleNextStep = () => {
		if (currentStep < 2) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePreviousStep = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		} else {
			// If we're on step 0 (material), go back to facade selection
			handleBackToFacadeChoice();
		}
	};

	// Enhanced handlers that include step navigation
	const handleFacadeSelectionWithStep = (facadeId) => {
		handleFacadeSelection(facadeId);
		// Auto-advance to next step if not on last step
		if (currentStep < 2) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleHouseSizeSelectionWithStep = (houseSizeId) => {
		handleHouseSizeSelection(houseSizeId);
		// Auto-advance to next step if not on last step
		if (currentStep < 2) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleWindowSelectionWithStep = (windowId) => {
		handleWindowSelection(windowId);
		// Auto-advance to next step if not on last step
		if (currentStep < 2) {
			setCurrentStep(currentStep + 1);
		}
	};


	// Reset step when entering house selection flow
	useEffect(() => {
		if (!showFacadeChoiceButtons && currentStep === 0) {
			// This ensures we start at step 0 when entering the house selection flow
		}
	}, [showFacadeChoiceButtons, currentStep]);

	// Function to render the current step component
	const renderCurrentStep = () => {
		switch (currentStep) {
			case 0:
				return (
					<HouseMaterialMobile
						selectedFacade={facade}
						onFacadeClick={handleFacadeSelectionWithStep}
					/>
				);
			case 1:
				return (
					<HouseSizeMobile
						selectedHouseSize={houseSize}
						onSizeClick={handleHouseSizeSelectionWithStep}
					/>
				);
			case 2:
				return (
					<WindowChoiceMobile
						selectedWindow={windows}
						onWindowClick={handleWindowSelectionWithStep}
					/>
				);
			default:
				return null;
		}
	};


  
	return (
		<Box style={{ 
			height: '100vh', 
			width: '100vw',
			display: 'flex', 
			flexDirection: 'column',
      backgroundColor: colors.mobileBackground,
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			overflow: 'hidden',
			boxSizing: 'border-box'
		}}>

			{/* Header */}
			<Box style={{
				backgroundColor: colors.backgroundLight,
        height: '120px',
				padding: '10px 10px',
				zIndex: 10,
				position: 'relative',
        borderBottom: `12px solid ${colors.white}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center'
			}}>
				<Text style={{
					color: white,
					fontSize: fontSize.xxxl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Your House
				</Text>
			</Box>

			{/* Navigation Bar */}
			<Group justify="space-between" align="center" style={{ 
				padding: '20px 25px',
				zIndex: 10,
				position: 'relative',
				height: '84px', // 20px padding top + 44px button height + 20px padding bottom
				flexShrink: 0
			}}>
				<Button
					variant="filled"
					onClick={handleBack}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							fontWeight: 'bold',
              minHeight: '44px',
              fontSize: fontSize.lg,
						}
					}}
				>
					Back
				</Button>
				
				<Button
					variant="filled"
					onClick={handleNext}
					disabled={!isHouseEditorDone}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							fontWeight: 'bold',
              minHeight: '44px',
              fontSize: fontSize.lg,
							opacity: isHouseEditorDone ? 1 : 0.5
						}
					}}
				>
					Next
				</Button>
			</Group>

			{/* Main Content Area - PreviewSVG Background */}
			<Box 
				ref={containerRef}
				style={{ 
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					paddingBottom: '200px' // Space for the fixed bottom panel
				}}
			>
				{/* PreviewSVG fills the space as background */}
				<Box style={{
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden'
				}}>
					<PreviewSVG styleOverride={{ width: '130%', marginLeft: '-15%' }} />
				</Box>
			</Box>

			{/* Bottom Selection Panel - Fixed Position */}
			<Box style={{
				backgroundColor: colors.darkerGrey,
				borderRadius: 20,
				padding: '20px',
        paddingTop: '20px',
        paddingBottom: '10px',
        paddingLeft: '10px',
        paddingRight: '10px',
        width: '90vw',
        height: showFacadeChoiceButtons ? '25vh' : '28vh',
        marginLeft: '5vw',
				//margin: '0 0px',
				position: 'fixed',
				bottom: 20,
				left: 0,
				right: 0,
				zIndex: 1000,
			}}>
					{showFacadeChoiceButtons ? (
						<Stack gap="15px">
							{/* House and Fence buttons in a row */}
							<Group justify="space-between" style={{ marginBottom: '10px' }}>
								<Button
									variant="filled"
									onClick={handleHouseClick}
									styles={{
										root: {
											backgroundColor: !['fence_wooden', 'none'].includes(facade) ? colors.selectedLightGrey : colors.altLightGrey,
											border: !['fence_wooden', 'none'].includes(facade) ? '2px solid #666' : '2px solid #475a46',
											borderRadius: '10px',
											height: '9vh',
											width: '35vw',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}
									}}
								>
									<img src={houseButton} alt='House' style={{ width: '12vw', height: '5.5vh' }} />
								</Button>
								
								<Button
									variant="filled"
									onClick={handleFenceClick}
									styles={{
										root: {
											backgroundColor: facade == 'fence_wooden' ? colors.selectedLightGrey : colors.altLightGrey,
											border: facade == 'fence_wooden' ? '2px solid #475a46' : '2px solid #666',
											borderRadius: '10px',
											height: '9vh',
											width: '35vw',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}
									}}
								>
									<img src={fenceButton} alt='Fence' style={{ width: '13vw', height: '4.7vh' }} />
								</Button>
							</Group>
							
							{/* None button centered */}
							<Center>
								<Button
									variant="filled"
									onClick={() => handleNoneClick()}
									styles={{
										root: {
											backgroundColor: facade == 'none' ? colors.selectedLightGrey : colors.altLightGrey,
											border: safeFacade === 'none' ? '2px solid #475a46' : '2px solid #666',
											borderRadius: '10px',
											height: '6vh',
											width: '40vw',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}
									}}
								>
									<Text style={{
										fontSize: Constants.fontSize.xl,
										fontWeight: 'bold',
										color: '#4d4d4d'
									}}>
										None
									</Text>
								</Button>
							</Center>
						</Stack>
					) : (
						<Stack gap="0px" style={{ height: '100%' }}>
							{/* Step Title */}
							{/*<Text style={{
								color: white,
								fontSize: fontSize.lg,
								fontWeight: 'bold',
								textAlign: 'center',
								marginBottom: '10px'
							}}>
								{getStepTitle()}
							</Text> */}
							
							{/* Current Step Component */}
							<Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: -20 }}>
								{renderCurrentStep()}
							</Box>
							
							{/* Navigation Buttons */}
							<Group justify="center" gap="0vw" style={{ marginTop: 'auto' }}>
								<Button
									variant="light"
									onClick={handlePreviousStep}
									styles={{
										root: {
                      backgroundColor: colors.darkerGrey,
											color: colors.white,
											border: 'none',
											fontWeight: 'bold',
											minHeight: '44px',
											fontSize: fontSize.xl,
                      width: '35vw',
										}
									}}
								>
									{'< Previous'}
								</Button>
								
								<Button
									variant="filled"
									onClick={handleNextStep}
									disabled={currentStep === 2}
									styles={{
										root: {
											backgroundColor: colors.darkerGrey,
											color: colors.white,
											border: 'none',
											fontWeight: 'bold',
											minHeight: '44px',
											fontSize: fontSize.xl,
											opacity: currentStep === 2 ? 0.5 : 1,
                      width: '35vw',
										}
									}}
								>
									{'Next >'}
								</Button>
							</Group>
							
							{/* Back to Facade Choice Button */}
              {/*
							<Button
								variant="subtle"
								onClick={handleBackToFacadeChoiceWithStep}
								styles={{
									root: {
										marginTop: '10px',
										color: Constants.colors.primary,
										textDecoration: 'underline',
										fontSize: fontSize.md
									}
								}}
							>
								Back to Facade Choice
							</Button> */}
						</Stack>
					)}
			</Box>
		</Box>
	);
};

HouseEditorMobile.propTypes = {
	// State functions
	isHouseEditorDone: PropTypes.bool,
	showFacadeChoiceButtons: PropTypes.bool,
	facade: PropTypes.string,
	houseSize: PropTypes.string,
	windows: PropTypes.string,
	
	// Handle functions
	handleNext: PropTypes.func.isRequired,
	handleBack: PropTypes.func.isRequired,
	handleHouseClick: PropTypes.func.isRequired,
	handleFenceClick: PropTypes.func.isRequired,
	handleNoneClick: PropTypes.func.isRequired,
	handleBackToFacadeChoice: PropTypes.func.isRequired,
	handleFacadeSelection: PropTypes.func.isRequired,
	handleHouseSizeSelection: PropTypes.func.isRequired,
	handleWindowSelection: PropTypes.func.isRequired
};

export default HouseEditorMobile;
